import { CacheAdapter, RateLimit } from '../types'
import { RateLimitError } from '../errors'

/**
 * Rate limit quota tracking - stores current usage
 */
export interface RateLimitQuota {
  count: number
  resetAt: Date
  limit: number
}

/**
 * Rate limiting engine - enforces quota limits using a cache backend
 * Implements token bucket algorithm with sliding window expiration
 */
export class RateLimiter {
  private cache: CacheAdapter
  private enabled: boolean

  constructor(cache: CacheAdapter | undefined, enabled: boolean = true) {
    this.cache = cache!
    this.enabled = enabled && !!cache
  }

  /**
   * Check if principal has exceeded rate limit
   * Returns remaining quota or throws RateLimitError
   */
  async checkLimit(principalId: string, rateLimit: RateLimit): Promise<RateLimitQuota> {
    if (!this.enabled || !this.cache) {
      // If caching is disabled, allow unlimited requests
      return {
        count: 0,
        resetAt: new Date(Date.now() + rateLimit.periodSec * 1000),
        limit: rateLimit.limit
      }
    }

    const key = `ratelimit:${principalId}`

    try {
      // Get current quota
      const existing = await this.cache.get(key)
      const now = new Date()

      // If no quota exists or it expired, create new one
      if (!existing) {
        const resetAt = new Date(now.getTime() + rateLimit.periodSec * 1000)
        const quota: RateLimitQuota = {
          count: 1,
          resetAt,
          limit: rateLimit.limit
        }

        await this.cache.set(key, quota, rateLimit.periodSec)
        return quota
      }

      // Parse existing quota
      const quota: RateLimitQuota = typeof existing === 'string' ? JSON.parse(existing) : existing

      // If quota has expired, reset it
      if (new Date(quota.resetAt) <= now) {
        const resetAt = new Date(now.getTime() + rateLimit.periodSec * 1000)
        const newQuota: RateLimitQuota = {
          count: 1,
          resetAt,
          limit: rateLimit.limit
        }

        await this.cache.set(key, newQuota, rateLimit.periodSec)
        return newQuota
      }

      // Check if limit exceeded
      if (quota.count >= quota.limit) {
        throw new RateLimitError(
          `Rate limit exceeded: ${quota.count}/${quota.limit} requests in current window`,
          quota.resetAt
        )
      }

      // Increment counter
      quota.count += 1
      const ttl = Math.ceil((new Date(quota.resetAt).getTime() - now.getTime()) / 1000)

      if (ttl > 0) {
        await this.cache.set(key, quota, ttl)
      }

      return quota
    } catch (error) {
      if (error instanceof RateLimitError) {
        throw error
      }

      // If cache fails, fail open (allow request but log error)
      console.error('Rate limiter cache error:', error)
      return {
        count: 0,
        resetAt: new Date(Date.now() + rateLimit.periodSec * 1000),
        limit: rateLimit.limit
      }
    }
  }

  /**
   * Reset rate limit for a principal (admin operation)
   */
  async reset(principalId: string): Promise<void> {
    if (!this.enabled || !this.cache) {
      return
    }

    const key = `ratelimit:${principalId}`
    await this.cache.del(key)
  }

  /**
   * Get current quota status for a principal
   */
  async getStatus(principalId: string): Promise<RateLimitQuota | null> {
    if (!this.enabled || !this.cache) {
      return null
    }

    const key = `ratelimit:${principalId}`

    try {
      const existing = await this.cache.get(key)
      if (!existing) {
        return null
      }

      return typeof existing === 'string' ? JSON.parse(existing) : existing
    } catch (error) {
      console.error('Error retrieving rate limit status:', error)
      return null
    }
  }

  /**
   * Check if rate limiting is enabled
   */
  isEnabled(): boolean {
    return this.enabled
  }
}

/**
 * Rate limit key generator - determines the key for rate limiting
 * Can be based on principal ID, API key, IP address, or combination
 */
export interface RateLimitKeyOptions {
  principalId?: string
  apiKeyId?: string
  ipAddress?: string
  userId?: string
  orgId?: string
}

/**
 * Generate consistent rate limit key
 */
export function generateRateLimitKey(options: RateLimitKeyOptions): string {
  // Prioritize principalId, then API key, then user/org, then IP
  if (options.principalId) {
    return options.principalId
  }

  if (options.apiKeyId) {
    return `apikey:${options.apiKeyId}`
  }

  if (options.userId) {
    return `user:${options.userId}`
  }

  if (options.orgId) {
    return `org:${options.orgId}`
  }

  if (options.ipAddress) {
    return `ip:${options.ipAddress}`
  }

  throw new Error('At least one rate limit key identifier must be provided')
}

/**
 * Format rate limit response headers
 */
export interface RateLimitHeaders {
  'X-RateLimit-Limit': string
  'X-RateLimit-Remaining': string
  'X-RateLimit-Reset': string
  'Retry-After'?: string
}

/**
 * Generate HTTP headers for rate limit info
 */
export function generateRateLimitHeaders(quota: RateLimitQuota): RateLimitHeaders {
  const remaining = Math.max(0, quota.limit - quota.count)
  const resetAt = new Date(quota.resetAt)
  const retryAfter = Math.ceil((resetAt.getTime() - Date.now()) / 1000)

  return {
    'X-RateLimit-Limit': String(quota.limit),
    'X-RateLimit-Remaining': String(remaining),
    'X-RateLimit-Reset': String(Math.floor(resetAt.getTime() / 1000)),
    ...(retryAfter > 0 && { 'Retry-After': String(retryAfter) })
  }
}
